import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { CpuChipIcon } from '@heroicons/react/24/outline';
import {
  STATUS_COLORS,
  formatBytes,
  getGpuTempColor,
} from '../constants';

/**
 * GPU monitoring panel showing utilization, VRAM, temperature, and processes.
 */
export default function GPUPanel({ gpus = [], hostname = 'localhost' }) {
  const barsRef = useRef([]);

  // Animate GPU utilization bars
  useEffect(() => {
    if (gpus.length > 0 && barsRef.current.length > 0) {
      gpus.forEach((gpu, idx) => {
        const el = barsRef.current[idx];
        if (!el) return;
        gsap.to(el, {
          width: `${gpu.utilization_percent || 0}%`,
          duration: 0.8,
          ease: 'power2.out',
        });
      });
    }
  }, [gpus]);

  if (!gpus || gpus.length === 0) {
    return (
      <div className="gpu-panel gpu-panel-empty">
        <CpuChipIcon className="icon" />
        <span>No GPUs detected</span>
      </div>
    );
  }

  return (
    <div className="gpu-panel">
      <div className="gpu-panel-header">
        <h4>GPU Farm</h4>
        <span className="gpu-host">{hostname}</span>
      </div>

      <div className="gpu-list">
        {gpus.map((gpu, idx) => {
          const utilPercent = gpu.utilization_percent || 0;
          const memUsed = gpu.memory_used_bytes || 0;
          const memTotal = gpu.memory_total_bytes || 1;
          const memPercent = (memUsed / memTotal) * 100;
          const temp = gpu.temperature_c || 0;
          const tempColor = getGpuTempColor(temp);

          // Get utilization color
          const utilColor = utilPercent > 90
            ? STATUS_COLORS.critical
            : utilPercent > 70
              ? STATUS_COLORS.warning
              : STATUS_COLORS.healthy;

          return (
            <div key={idx} className="gpu-card">
              <div className="gpu-header">
                <span className="gpu-index">GPU {idx}</span>
                <span className="gpu-name">{gpu.name || 'Unknown'}</span>
              </div>

              {/* Utilization bar */}
              <div className="gpu-metric">
                <div className="gpu-metric-header">
                  <span className="metric-label">Utilization</span>
                  <span className="metric-value" style={{ color: utilColor }}>
                    {utilPercent.toFixed(0)}%
                  </span>
                </div>
                <div className="gpu-bar">
                  <div
                    className="gpu-bar-fill"
                    ref={(el) => (barsRef.current[idx] = el)}
                    style={{
                      width: '0%',
                      backgroundColor: utilColor,
                    }}
                  />
                </div>
              </div>

              {/* Memory and Temperature */}
              <div className="gpu-stats">
                <div className="gpu-stat">
                  <span className="stat-label">VRAM</span>
                  <span className="stat-value">
                    {formatBytes(memUsed)} / {formatBytes(memTotal)}
                  </span>
                </div>
                <div className="gpu-stat">
                  <span className="stat-label">Temp</span>
                  <span className="stat-value" style={{ color: tempColor }}>
                    {temp}C
                  </span>
                </div>
                {gpu.power_draw_w > 0 && (
                  <div className="gpu-stat">
                    <span className="stat-label">Power</span>
                    <span className="stat-value">
                      {gpu.power_draw_w.toFixed(0)}W
                    </span>
                  </div>
                )}
              </div>

              {/* Processes using GPU */}
              {gpu.processes && gpu.processes.length > 0 && (
                <div className="gpu-processes">
                  <span className="processes-label">Processes:</span>
                  {gpu.processes.map((proc, pIdx) => (
                    <span key={pIdx} className="process-item">
                      {proc.name} ({formatBytes(proc.memory_bytes)})
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
